<template>
  <div class="orientation-home jwl-home__row"  v-if="isAuthorised(rolesAndRightsList.RIGHT__ADDITIONAL_COURSES__DISPLAY, false)">
    <h2 class="jwl-home__headline-column" v-t="'home.additionalCourses'" />

    <div class="jwl-home__text-column">
      <div class="orientation-home__error" v-if="error">
        {{error.trans ? $t(error.message) : error.message}}
      </div>
      <div class="orientation-home__loading" v-if="loadingState < 2">
        <JwlIcon icon-name="spinner-third" icon-style="duotone" />
        {{$t('general.loadingData')}}
      </div>
      <template v-else-if="orientationUnits.length > 0">
        <ul class="orientation-home__list">
          <li class="orientation-home__list-item" v-for="unit in filteredUnits" :key="`unit-${unit.id}`">
          <span class="orientation-home__item-headline">
            {{unit.title}}
          </span>
            <span class="orientation-home__item-subheadline">
            {{unit.course.title}}
          </span>
            <JwlButton :to-route="{ name: 'orientation_unit', params: { courseId: unit.course.id, unit: unit.id }}" full-width>
              {{$t('course.unitDetails')}}
            </JwlButton>
          </li>
        </ul>
        <ul class="orientation-home__dot-list" v-if="hasDots">
          <li class="orientation-home__dot-item"
              :class="activeDotClass(index)"
              v-for="(unit, index) in orientationUnits"
              :key="`unit-dot-${unit.id}`">
            <button class="orientation-home__dot-button" @click="setIndex(index)" :title="unit.title">
              <span class="orientation-home__dot-bar" />
            </button>
          </li>
        </ul>
      </template>
      <div v-else class="orientation-home__empty-list" v-t="'home.additionalCoursesCompleted'" />
    </div>
  </div>
</template>

<script setup>
  const { isAuthorised, rolesAndRightsList } = useUserAuthorisation();

  const learningStore = useLearningStore();
  const loadingState = ref(0);
  const orientationUnits = ref([]);
  const error = ref(null);
  const currentIndex = ref(0);
  const visibleItems = 3;

  const fetchData = () => {
    loadingState.value = 1;
    learningStore.getData('orientation/units/home')
      .then((data) => {
        orientationUnits.value = shuffle(data);
        loadingState.value = 2;
      })
      .catch((e) => {
        error.value = e;
        loadingState.value = -1;
      });
  };

  const setIndex = (newIndex) => {
    if (newIndex > (orientationUnits.value.length - visibleItems)) {
      currentIndex.value = orientationUnits.value.length - visibleItems;
    } else {
      currentIndex.value = newIndex;
    }
  };

  const activeDotClass = (index) => ({
    'orientation-home__dot-item--active': index === currentIndex.value,
  });

  const shuffle = (array) => {
    for (let i = array.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const filteredUnits = computed(() => orientationUnits.value.slice(currentIndex.value, currentIndex.value + visibleItems));

  const hasDots = computed(() => loadingState.value === 2
      && orientationUnits.value.length > visibleItems);

  fetchData();
</script>

<style lang="scss">
@use "@base/styles/variables";
@use "@base/styles/mixins";

.orientation-home {
  &__loading {
    align-items: center;
    display: flex;

    svg {
      @include mixins.ltrtl(margin-right, margin-left, .5rem);

      color: var(--color-blue);
      font-size: 1.5rem;
    }
  }

  &__list {
    display: grid;
    grid-gap: .5rem;
    grid-template: "first second third" auto / 1fr 1fr 1fr;
    list-style: none;
    margin: 0 0;
    padding: 0 0;

    @include mixins.mq(variables.$mq-phone) {
      grid-template: "first" auto "secod" auto "third" auto / 100%;
    }

    .jwl-button {
      margin-top: auto;
    }
  }

  &__list-item {
    border: .16rem solid var(--color-blue);
    border-radius: var(--border-radius);
    color: var(--color-blue);
    display: flex;
    flex-flow: column;
    padding: 1rem 1.25rem;
  }

  &__item-headline {
    font-size: 1.25rem;
    margin-bottom: .75rem;
    text-transform: uppercase;
  }

  &__item-subheadline {
    font-weight: var(--font-weight-light);
    margin-bottom: .5rem;
  }

  &__dot-list {
    display: flex;
    justify-content: stretch;
    list-style: none;
    margin: .5rem 0 0;
    padding: 0 0;
    width: 100%;
  }

  &__dot-item {
    --dot-color: var(--color-gray-400);

    &:hover {
      --dot-color: var(--color-blue);
    }

    flex: 1 1 100%;

    &--active,
    &--active + &,
    &--active + & + & {
      --dot-color: var(--opaque-blue);
    }

    &:not(:last-child) {
      @include mixins.ltrtl(margin-right, margin-left, .25rem);
    }
  }

  &__dot-button {
    align-items: center;
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    justify-content: center;
    padding: .5rem 0;
    position: relative;
    width: 100%;

    @include mixins.mq(variables.$mq-phone) {
      max-width: 100%;
    }
  }

  &__dot-bar {
    background-color: var(--dot-color);
    border-radius: .2rem;
    height: .4rem;
    transition: background-color .25s;
    width: 100%;
  }

  &__empty-list {
    padding: .125rem 0 0;
  }
}
</style>
